import { Button, ButtonGroup, CoursesIcon, DeleteIcon, EditIcon, Table, type TableColumns } from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsDesktop } from '../../../hooks/useIsDesktop';

const AbsenceReasonTableFragment = graphql(`
  fragment AbsenceReasonTableFields on Query {
    absenceReasons(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      reason
      internal
      absencesAggregate {
        __typename
        count
      }
    }
  }
`);

type AbsenceReasonTableProps = {
  isLoading: boolean;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string) => void;
  data: FragmentType<typeof AbsenceReasonTableFragment> | undefined;
};

type AbsenceReasonTableType = {
  uuid: string;
  reason: string;
  isInternal: boolean;
};

export const AbsenceReasonTable = ({ isLoading, onEdit, onDelete, data }: AbsenceReasonTableProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();

  const fragment = getFragmentData(AbsenceReasonTableFragment, data);
  const absenceReasons = fragment?.absenceReasons ?? [];

  const columns: TableColumns<AbsenceReasonTableType>[] = useMemo(() => {
    return [
      {
        header: t('absences.reasons.title', { context: 'short' }),
        id: 'reason',
        accessorKey: 'reason',
        size: 450,
      },
      {
        header: t('absences.reasons.isInternal'),
        id: 'isInternal',
        accessorKey: 'isInternal',
        type: 'boolean',
        size: 100,
      },
    ];
  }, [t]);

  const tableData: AbsenceReasonTableType[] =
    absenceReasons.map((a) => ({
      uuid: a.uuid,
      reason: a.reason,
      isInternal: a.internal ?? false,
    })) ?? [];

  return (
    <Table<AbsenceReasonTableType>
      columns={columns}
      data={tableData}
      sorting={[{ id: 'reason', desc: false }]}
      customPadding='var(--spacing-7)'
      emptyStateSettings={{ icon: <CoursesIcon /> }}
      isDesktop={isDesktop}
      lastColWidth='68px'
      lastCol={(row) => {
        const reason = absenceReasons.find((a) => a.uuid === row.original.uuid);
        const isInUse = reason?.absencesAggregate?.count !== undefined && reason.absencesAggregate.count > 0;

        return (
          <ButtonGroup disabled={isInUse} tooltip={isInUse ? t('absences.isInUse') : undefined}>
            <Button
              hierarchy='secondary'
              type='button'
              onClick={() => onEdit(row.original.uuid)}
              icon={<EditIcon className='small' />}
            />
            <Button
              hierarchy='secondary'
              type='button'
              onClick={() => onDelete(row.original.uuid)}
              icon={<DeleteIcon className='small' color={!isInUse ? 'var(--color-error)' : undefined} />}
            />
          </ButtonGroup>
        );
      }}
      loading={isLoading}
    />
  );
};
